import type { Deal, Deadline } from './types'

export function formatMoney(value?: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value ?? 0)
}

export function formatDate(value?: string): string {
  if (!value) return 'Not set'
  const normalized = value.includes('T') ? value : `${value}T00:00:00`
  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) return 'Not set'
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function daysUntil(value?: string): number | null {
  if (!value) return null
  const target = new Date(value.includes('T') ? value : `${value}T00:00:00`)
  if (Number.isNaN(target.getTime())) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  target.setHours(0, 0, 0, 0)
  return Math.ceil((target.getTime() - today.getTime()) / 86_400_000)
}

export function titleCase(value?: string): string {
  if (!value) return 'Not set'
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

export function dealStatusTone(status?: string): 'success' | 'warning' | 'danger' | 'default' {
  switch (status) {
    case 'active':
    case 'closed':
      return 'success'
    case 'at_risk':
      return 'danger'
    case 'committed':
    case 'pending':
      return 'warning'
    case 'cancelled':
      return 'danger'
    default:
      return 'default'
  }
}

export function isOpenDealStatus(status?: string): boolean {
  return status === 'active' || status === 'committed' || status === 'at_risk'
}

export function deadlineStatusTone(status?: string): 'success' | 'warning' | 'danger' | 'default' {
  switch (status) {
    case 'completed':
    case 'waived':
      return 'success'
    case 'at_risk':
    case 'overdue':
      return 'danger'
    case 'extended':
      return 'warning'
    default:
      return 'default'
  }
}

export function isDealAtRisk(deal: Deal): boolean {
  if (deal.status === 'at_risk') return true
  const coeDays = daysUntil(deal.close_of_escrow_date)
  return isOpenDealStatus(deal.status) && coeDays !== null && coeDays <= 5 && coeDays >= 0
}

export function isDeadlineAtRisk(deadline: Deadline): boolean {
  const dueDays = daysUntil(deadline.due_date)
  return deadline.status !== 'completed' && deadline.status !== 'waived' && dueDays !== null && dueDays <= 2
}

export function compareSortValues(a: number | string, b: number | string): number {
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b
  }

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

export function getInitials(name: string, fallback = ''): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.slice(0, 1).toUpperCase())
      .join('') || fallback
  )
}
